import MenuItem, { ButtonContainer, LinkContainer } from "@components/MenuItem"
import animation, { cubicBezier } from "@constants/animation"
import colors from "@constants/colors"
import { getIn } from "@utils/index"
import { MenuDataProps } from "@utils/types"
import { getShopURLForBrandDeviceType, getShopURLForDevice } from "@utils/urls"
import _ from "lodash-es"
import * as R from "ramda"
import React from "react"
import styled from "styled-components"

import { ActionLink, BreadcrumbButton, Headers } from "./common"

const openProps = `
  transform: translateX(0);
  transition: transform ${animation.medium} ${cubicBezier};
`
const Container = styled.div<{ $isOpen?: boolean }>`
  position: absolute;
  top: 0;
  width: 100%;
  height: 100%;
  opacity: 1;
  transform: translateX(100%);
  transition: transform ${animation.medium} ${cubicBezier};
  will-change: transform;
  background-color: ${colors.offWhite};
  overflow: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  z-index: 5;
  cursor: default;
  ${({ $isOpen }) => ($isOpen ? openProps : "")}
`

const overlayActiveProps = `
  opacity: 0.1
  transition: opacity ${animation.medium} ${cubicBezier};
`
const Overlay = styled.div<{ $isVisible: boolean }>`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  height: 100%;
  width: 100%;
  will-change: opacity;
  opacity: 0;
  transition: opacity ${animation.kindaLong} ${cubicBezier};
  background-color: ${colors.black};
  pointer-events: none;
  z-index: 4;
  ${({ $isVisible }) => ($isVisible ? overlayActiveProps : "")}
`

const Devices = styled.div`
  overflow: hidden;

  ${ButtonContainer} {
    color: ${colors.black};

    svg {
      stroke: ${colors.black};
    }
  }

  ${LinkContainer} {
    color: ${colors.black};

    svg {
      stroke: ${colors.black};
    }
  }
`

interface SelectDeviceProps {
  brandSlug?: string
  data: MenuDataProps
  deviceTypeSlug?: string
  onRequestClose?: () => void
}

const SelectDevice: React.FC<SelectDeviceProps> = ({
  brandSlug,
  data,
  deviceTypeSlug,
  onRequestClose,
}) => {
  const handleClose = () => {
    if (onRequestClose) {
      onRequestClose()
    }
  }

  const formatDevice = ({ name, new: isNew, slug }) => {
    const shopURL = getShopURLForDevice(slug)
    if (window && getIn(window, "location.pathname") === shopURL) {
      return {
        label: name,
        onClick: handleClose,
      }
    }

    return {
      label: name,
      link: shopURL,
      slug,
      tag: isNew ? "New" : null,
    }
  }

  const allBrands = getIn(data, `${deviceTypeSlug}.brands`, [])
  const brandData = R.find(
    (brand: any) => brandSlug === brand.brandSlug,
    allBrands
  )
  const devices = _.uniqBy(getIn(brandData, "devices", []), "slug") as any
  const formattedDevices = React.useMemo(
    () => devices.map(formatDevice),
    [deviceTypeSlug, brandSlug, data]
  )

  return (
    <>
      <Overlay $isVisible={!!deviceTypeSlug && !!brandSlug} />
      <Container $isOpen={!!deviceTypeSlug && !!brandSlug}>
        <Headers>
          <BreadcrumbButton
            label={getIn(brandData, "brandName", "")}
            onClick={onRequestClose}
          />
          {brandSlug && deviceTypeSlug && (
            <ActionLink
              to={getShopURLForBrandDeviceType(brandSlug, deviceTypeSlug)}
            >
              All
            </ActionLink>
          )}
        </Headers>
        <Devices>
          {formattedDevices.map((props) => (
            <MenuItem key={`${deviceTypeSlug}-${props.slug}`} {...props} />
          ))}
        </Devices>
      </Container>
    </>
  )
}

export default SelectDevice
